import type { TablerIcon } from "@tabler/icons-react";
import type { ComponentPropsWithoutRef, ElementType, ReactNode } from "react";
import { tokenByPath } from "../styles/typographyTokens";
import styles from "./Button.module.css";

const buttonDefaultType = tokenByPath("Button/Default")!.className;

export type ButtonVariant = "primary" | "secondary" | "outline";

const ICON_SIZE = 24;

type ButtonOwnProps<E extends ElementType> = {
  variant?: ButtonVariant;
  /** Stretch to the container width (marketing / auth / checkout CTAs). Ignored for icon-only buttons. */
  fullWidth?: boolean;
  /**
   * Tabler icon for an icon-only 48×48 square (24 px glyph). Works with any
   * `variant` (primary / secondary / outline). Alternative: pass a rendered
   * icon as `children` with `iconOnly`.
   */
  icon?: TablerIcon;
  /**
   * Icon-only layout (48×48 square) when passing the glyph as `children`
   * instead of the `icon` prop.
   */
  iconOnly?: boolean;
  as?: E;
  className?: string;
  children?: ReactNode;
};

export type ButtonProps<E extends ElementType = "button"> = ButtonOwnProps<E> &
  Omit<ComponentPropsWithoutRef<E>, keyof ButtonOwnProps<E>>;

/**
 * Self-contained CTA button shared by marketing, modal, auth and checkout
 * surfaces. Icon-only buttons are a 48×48 px square with a 24 px Tabler
 * glyph — set `icon={IconX}` or `iconOnly` + the icon as `children`, and
 * always provide `aria-label`.
 */
export function Button<E extends ElementType = "button">({
  variant = "primary",
  fullWidth = false,
  icon: IconComponent,
  iconOnly = false,
  as,
  className,
  children,
  ...rest
}: ButtonProps<E>) {
  const Component = (as ?? "button") as ElementType;
  const isIconOnly = Boolean(IconComponent) || iconOnly;
  const classes = [
    isIconOnly ? "" : buttonDefaultType,
    styles.btn,
    styles[variant],
    isIconOnly ? styles.iconOnly : "",
    fullWidth && !isIconOnly ? styles.fullWidth : "",
    className ?? ""
  ]
    .filter(Boolean)
    .join(" ");

  const typeProp =
    Component === "button" && (rest as { type?: string }).type === undefined
      ? { type: "button" as const }
      : {};

  const content = IconComponent ? (
    <IconComponent size={ICON_SIZE} stroke={1.5} aria-hidden />
  ) : (
    children
  );

  return (
    <Component className={classes} {...typeProp} {...rest}>
      {content}
    </Component>
  );
}

export {
  StickyButton,
  type StickyButtonLayout,
  type StickyButtonProps
} from "./StickyButton";

export default Button;
